import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { ContactInfo, UserProfile } from '../types';
import { useSubscriptionStore } from './subscriptionStore';
import { resetPassword } from '../utils/auth';

interface AuthState {
  user: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isRecoverySession: boolean;
  contactInfo: ContactInfo | null;
  
  // Actions
  setContactInfo: (info: ContactInfo) => void;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ error: null }>;
  completePasswordReset: (newPassword: string) => Promise<{ error: Error | null }>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

const isRecoveryHash = () =>
  typeof window !== 'undefined' && window.location.hash.includes('type=recovery');

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: true,
  error: null,
  isAuthenticated: false,
  isRecoverySession: false,
  contactInfo: null,

  setContactInfo: (info) => {
    set({ contactInfo: info });
  },

  signIn: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      // First try to refresh any existing session
      await supabase.auth.refreshSession();
      
      const { data: { user: authUser }, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) {
        throw signInError;
      }

      if (!authUser) throw new Error('No user returned from auth');

      // Get user profile from database
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (profileError) {
        throw new Error('Failed to get user profile');
      }

      // Create user profile from database data
      const userProfile: UserProfile = {
        id: authUser.id,
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        accountType: profile.account_type,
        subscriptionTier: profile.subscription_tier,
        projects: [],
        notifications: [],
        paymentHistory: []
      };

      // Set subscription tier in subscription store
      await useSubscriptionStore.getState().setCurrentTier(userProfile.subscriptionTier ?? 'free');

      set({ 
        user: userProfile,
        isAuthenticated: true,
        error: null
      });

      return { data: { user: userProfile }, error: null };
    } catch (error: any) {
      set({ 
        error: error?.message || 'Failed to sign in',
        user: null,
        isAuthenticated: false
      });
      return { data: null, error };
    } finally {
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      set({ 
        user: null, 
        isAuthenticated: false,
        isRecoverySession: false,
        error: null
      });
    } catch (error: any) {
      set({ error: error?.message || 'Failed to sign out' });
    } finally {
      set({ isLoading: false });
    }
  },

  requestPasswordReset: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await resetPassword(email);
      if (error) {
        console.warn('Password reset request error:', error);
      }
      return { error: null };
    } finally {
      set({ isLoading: false });
    }
  },

  completePasswordReset: async (newPassword: string) => {
    set({ isLoading: true, error: null });
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      await supabase.auth.signOut({ scope: 'others' });
      await supabase.auth.signOut();

      set({
        user: null,
        isAuthenticated: false,
        isRecoverySession: false,
        error: null,
      });

      return { error: null };
    } catch (error: any) {
      set({ error: error?.message || 'Failed to update password' });
      return { error };
    } finally {
      set({ isLoading: false });
    }
  },

  checkAuth: async () => {
    set({ isLoading: true });
    try {
      if (get().isRecoverySession || isRecoveryHash()) {
        set({
          isRecoverySession: true,
          user: null,
          isAuthenticated: false,
          error: null,
          isLoading: false,
        });
        return;
      }

      // First try to refresh the session
      const { error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.warn('Session refresh error:', refreshError);
      }

      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.warn('Session error:', sessionError);
        set({ 
          user: null,
          isAuthenticated: false,
          error: null,
          isLoading: false
        });
        return;
      }

      if (!currentSession) {
        set({ 
          user: null,
          isAuthenticated: false,
          error: null,
          isLoading: false
        });
        return;
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        set({ 
          user: null,
          isAuthenticated: false,
          error: null,
          isLoading: false
        });
        return;
      }
      
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        // If profile doesn't exist yet, create it from user metadata
        if (profileError.code === 'PGRST116') {
          const userData = user.user_metadata || {};
          
          const { data: newProfile, error: createError } = await supabase
            .from('users')
            .insert({
              id: user.id,
              email: user.email,
              name: userData.name || user.email?.split('@')[0] || 'User',
              phone: userData.phone || '',
              account_type: userData.accountType || 'individual',
              subscription_tier: 'free',
              verified: true
            })
            .select()
            .single();
            
          if (createError) {
            console.error('Error creating user profile:', createError);
            set({ 
              user: null,
              isAuthenticated: false,
              error: null,
              isLoading: false
            });
            return;
          }
          
          const userProfile: UserProfile = {
            id: user.id,
            name: newProfile.name,
            email: newProfile.email,
            phone: newProfile.phone,
            accountType: newProfile.account_type,
            subscriptionTier: newProfile.subscription_tier,
            projects: [],
            notifications: [],
            paymentHistory: []
          };
          
          await useSubscriptionStore.getState().setCurrentTier(userProfile.subscriptionTier ?? 'free');
          
          set({ 
            user: userProfile,
            isAuthenticated: true,
            error: null
          });
          return;
        }
        
        console.warn('Profile fetch error:', profileError);
        set({ 
          user: null,
          isAuthenticated: false,
          error: null,
          isLoading: false
        });
        return;
      }

      const userProfile: UserProfile = {
        id: user.id,
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        accountType: profile.account_type,
        subscriptionTier: profile.subscription_tier,
        projects: [],
        notifications: [],
        paymentHistory: []
      };

      await useSubscriptionStore.getState().setCurrentTier(userProfile.subscriptionTier ?? 'free');

      set({ 
        user: userProfile,
        isAuthenticated: true,
        error: null
      });
    } catch (error) {
      console.warn('Auth check error:', error);
      set({ 
        user: null,
        isAuthenticated: false,
        error: null
      });
    } finally {
      set({ isLoading: false });
    }
  },

  clearError: () => set({ error: null })
}));

supabase.auth.onAuthStateChange((event) => {
  if (event === 'PASSWORD_RECOVERY') {
    useAuthStore.setState({
      isRecoverySession: true,
      user: null,
      isAuthenticated: false,
    });
  }
  if (event === 'SIGNED_OUT') {
    useAuthStore.setState({ isRecoverySession: false });
  }
});

// Initialize auth check
useAuthStore.getState().checkAuth();
